/**
 * Environment configuration utility
 * Handles dynamic environment detection for Cloudflare preview deployments
 */

export interface EnvironmentConfig {
  apiUrl: string;
  publicUrl: string;
  isPreview: boolean;
  isProduction: boolean;
  isRelease: boolean;
  isDevelopment: boolean;
}

/**
 * Detects if the current environment is a Cloudflare preview deployment
 */
function isCloudflarePreview(): boolean {
  if (typeof window === 'undefined') return false;

  const hostname = window.location.hostname;
  return hostname.includes('.workers.dev') || hostname.includes('.pages.dev');
}

/**
 * Gets the Cloudflare Pages URL from environment variables
 */
function getCloudflarePagesUrl(): string | null {
  // CF_PAGES_URL is available during build time in Cloudflare Pages
  return process.env.CF_PAGES_URL || null;
}

/**
 * Gets the appropriate API URL based on the current environment
 */
function getApiUrl(): string {
  // TODO: Here we can possibly use dynamic API URL for preview environments. Future thing...
  // if (isCloudflarePreview()) {
  //   return `${window.location.protocol}//${window.location.host}/`;
  // }

  return process.env.REACT_APP_API_URL || '';
}

/**
 * Gets the appropriate public URL based on the current environment
 */
function getPublicUrl(): string {
  // First check if we have CF_PAGES_URL from build time
  const cfPagesUrl = getCloudflarePagesUrl();
  if (cfPagesUrl) {
    return cfPagesUrl.endsWith('/') ? cfPagesUrl : `${cfPagesUrl}/`;
  }

  // Fallback to runtime detection for Cloudflare preview
  if (isCloudflarePreview()) {
    return `${window.location.protocol}//${window.location.host}/`;
  }

  return process.env.REACT_APP_PUBLIC_URL || '';
}

/**
 * Gets the current environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const isPreview = isCloudflarePreview();
  const env = (process.env.REACT_APP_ENV || '').trim().toLowerCase();
  const isProduction = env === 'production';
  const isRelease = env === 'release';
  const isDevelopment = env === 'development';

  return {
    apiUrl: getApiUrl(),
    publicUrl: getPublicUrl(),
    isPreview,
    isProduction,
    isRelease,
    isDevelopment,
  };
}

/**
 * Hook for React components to access environment configuration
 */
export function useEnvironment(): EnvironmentConfig {
  return getEnvironmentConfig();
}
